import {
  RESOURCE_MAP_VISIBLE_EDGE_CAP,
  placeResourceMapNodes,
  type PlacedNode,
  type ResourceMapGraphEdge,
  type ResourceMapGraphNode,
} from "./resource-map-layout";

/**
 * 시각 미리보기와 텍스트 대체가 **같이 보는** 유계 부분집합 (ADR 0025 · Issue #13)
 * --------------------------------------------------------------------------
 * 그림과 대체 목록이 각자 상한을 계산하면 두 표현이 서로 다른 것을 말합니다 —
 * 그림에는 있는데 목록에는 없는 노드가 생기는 순간, 대체는 대체가 아니라 거짓말이
 * 됩니다. 그래서 부분집합 계산은 여기 한 번뿐이고 둘 다 결과를 그대로 받습니다.
 *
 * 서버 배열 순서를 보존하고 O(N+E)입니다. 정렬·난수·타이머가 없습니다.
 */
export interface BoundedGraph {
  /** 배치된 노드(상한 안). focus는 언제나 포함됩니다. */
  placed: PlacedNode[];
  /** 배치된 노드 사이의 간선만, 상한까지. */
  edges: ResourceMapGraphEdge[];
  /** key → 배치 정보. 간선 좌표 계산과 목록의 노드 조회가 함께 씁니다. */
  byKey: Map<string, PlacedNode>;
  /** key → 이 부분집합에서의 연결 수. 목록이 "연결 n건"을 말할 때 씁니다. */
  degree: Map<string, number>;
  /** 생략된 개수. 0이어도 화면이 "전부 보여준다"고 단정하지 않습니다. */
  omittedNodes: number;
  omittedEdges: number;
}

export function boundedGraph(
  nodes: ReadonlyArray<ResourceMapGraphNode>,
  edges: ReadonlyArray<ResourceMapGraphEdge>,
): BoundedGraph {
  const placed = placeResourceMapNodes(nodes);
  const byKey = new Map<string, PlacedNode>();
  for (const entry of placed) byKey.set(entry.node.key, entry);

  const visible: ResourceMapGraphEdge[] = [];
  const degree = new Map<string, number>();
  for (const edge of edges) {
    if (visible.length >= RESOURCE_MAP_VISIBLE_EDGE_CAP) break;
    if (!byKey.has(edge.from) || !byKey.has(edge.to)) continue;
    visible.push(edge);
    degree.set(edge.from, (degree.get(edge.from) ?? 0) + 1);
    degree.set(edge.to, (degree.get(edge.to) ?? 0) + 1);
  }

  return {
    placed,
    edges: visible,
    byKey,
    degree,
    omittedNodes: nodes.length - placed.length,
    omittedEdges: edges.length - visible.length,
  };
}
